import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { useToast } from "@/hooks/use-toast";
import type {
  NegotiationThread,
  NegotiationMessage,
  NegotiationMode,
  VendorTerms,
} from "@/types/negotiations";

interface ThreadDetail {
  thread: NegotiationThread | null;
  messages: NegotiationMessage[];
}

async function fetchThreadDetail(id: string): Promise<ThreadDetail> {
  const [{ data: thread, error: te }, { data: messages, error: me }] = await Promise.all([
    supabase.from("negotiation_threads").select("*").eq("id", id).maybeSingle(),
    supabase
      .from("negotiation_messages")
      .select("*")
      .eq("thread_id", id)
      .order("created_at", { ascending: true }),
  ]);

  if (te) throw te;
  if (me) throw me;

  return {
    thread: (thread as NegotiationThread) ?? null,
    messages: (messages ?? []) as NegotiationMessage[],
  };
}

export function useNegotiationThread(id: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const queryKey = ["negotiation_thread", id];

  // Track which thread IDs have already had an initial draft requested so we
  // never fire more than once per thread, even across re-renders or refetches.
  const autoGenFiredRef = useRef<Set<string>>(new Set());
  // True while the auto-generate call is in-flight so the UI can show a
  // "Generating draft…" state rather than the static empty-thread placeholder.
  const [isGeneratingInitialDraft, setIsGeneratingInitialDraft] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchThreadDetail(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  // Realtime subscription for messages on this thread
  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`negotiation-thread-${id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "negotiation_messages",
          filter: `thread_id=eq.${id}`,
        },
        () => refetch()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "negotiation_threads", filter: `id=eq.${id}` },
        () => refetch()
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [id, refetch]);

  // Auto-generate an AI draft in two scenarios:
  //
  // 1. INITIAL draft — thread has no messages at all.
  //    Fires: invokeEdgeFunction("negotiation-draft", { thread_id, mode: "initial" })
  //
  // 2. REPLY draft — thread status is "needs_attention" (vendor replied, operator
  //    owes a response) but no draft/pending_approval message exists yet. This
  //    handles the case where the inbound flow failed to create the draft.
  //    Fires: invokeEdgeFunction("negotiation-draft", { thread_id, mode: "reply" })
  //
  // Both paths are guarded by the same `autoGenFiredRef` Set (keyed by thread id)
  // so only one fires per thread and it never repeats across re-renders/refetches.
  useEffect(() => {
    if (!id) return;
    if (isLoading) return;
    if (!data?.thread) return;
    // If we already fired for this thread id, skip.
    if (autoGenFiredRef.current.has(id)) return;

    const messages = data?.messages ?? [];
    const hasDraft = messages.some((m) => m.status === "draft" || m.status === "pending_approval");

    let mode: "initial" | "reply" | null = null;

    if (messages.length === 0) {
      // Scenario 1: empty thread — generate the opening message.
      mode = "initial";
    } else if (data.thread.status === "needs_attention" && !hasDraft) {
      // Scenario 2: vendor replied but no AI draft exists — generate a reply.
      mode = "reply";
    }

    if (!mode) return;

    // Mark as fired before the async call to prevent concurrent duplicate invocations.
    autoGenFiredRef.current.add(id);
    setIsGeneratingInitialDraft(true);

    const errorTitle =
      mode === "initial" ? "Failed to generate initial draft" : "Failed to generate reply draft";

    invokeEdgeFunction<{ success: boolean; message: NegotiationMessage }>("negotiation-draft", {
      thread_id: id,
      mode,
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey });
      })
      .catch((err: Error) => {
        toast({
          title: errorTitle,
          description: err.message,
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsGeneratingInitialDraft(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isLoading, data?.messages?.length, data?.thread?.status, data?.thread]);

  const approveSendMutation = useMutation({
    mutationFn: async (message_id: string) => {
      return invokeEdgeFunction<{ success: boolean; message: NegotiationMessage }>(
        "negotiation-send",
        {
          message_id,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
      toast({ title: "Message sent" });
    },
    onError: (err) => {
      toast({
        title: "Failed to send",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const discardDraftMutation = useMutation({
    mutationFn: async (message_id: string) => {
      // Accept both 'draft' and 'pending_approval' — AI-generated drafts arrive
      // with status='pending_approval', not 'draft'. Filtering on 'draft' alone
      // causes the delete to match 0 rows and the message is never removed.
      const { error } = await supabase
        .from("negotiation_messages")
        .delete()
        .eq("id", message_id)
        .in("status", ["draft", "pending_approval"]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Draft discarded" });
    },
    onError: (err) => {
      toast({
        title: "Failed to discard",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const regenerateDraftMutation = useMutation({
    mutationFn: async (thread_id: string) => {
      return invokeEdgeFunction<{ success: boolean; message: NegotiationMessage }>(
        "negotiation-draft",
        {
          thread_id,
          mode: "reply",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "New draft generated" });
    },
    onError: (err) => {
      toast({
        title: "Failed to regenerate",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  /**
   * Manually trigger an AI draft at any time. Chooses mode:"initial" when the
   * thread has no messages, and mode:"reply" when it already has at least one.
   * Unlike the auto-gen effect this can be called repeatedly by the operator.
   */
  const generateDraftMutation = useMutation({
    mutationFn: async (thread_id: string) => {
      const messages = data?.messages ?? [];
      const mode: "initial" | "reply" = messages.length === 0 ? "initial" : "reply";
      return invokeEdgeFunction<{ success: boolean; message: NegotiationMessage }>(
        "negotiation-draft",
        {
          thread_id,
          mode,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "AI response generated" });
    },
    onError: (err) => {
      toast({
        title: "Failed to generate AI response",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const editDraftMutation = useMutation({
    mutationFn: async ({
      message_id,
      updates,
    }: {
      message_id: string;
      updates: { subject?: string; body_text?: string };
    }) => {
      // When the user edits body_text, clear body_html so the send edge function
      // does not send the stale AI-generated HTML alongside the edited plain text.
      const payload: { subject?: string; body_text?: string; body_html?: null } = { ...updates };
      if ("body_text" in updates) {
        payload.body_html = null;
      }
      // Accept both 'draft' and 'pending_approval' — AI-generated drafts arrive
      // with status='pending_approval', not 'draft'. Filtering on 'draft' alone
      // causes the update to match 0 rows and edits are silently lost.
      const { error } = await supabase
        .from("negotiation_messages")
        .update(payload)
        .eq("id", message_id)
        .in("status", ["draft", "pending_approval"]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      toast({
        title: "Failed to save edit",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: async ({
      thread_id,
      agreed_apr,
      agreed_days_early,
    }: {
      thread_id: string;
      agreed_apr: number;
      agreed_days_early: number;
    }) => {
      return invokeEdgeFunction<{ success: boolean; vendor_terms: VendorTerms }>(
        "negotiation-finalize",
        { thread_id, agreed_apr, agreed_days_early }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
      toast({ title: "Deal finalized", description: "Vendor terms have been saved." });
    },
    onError: (err) => {
      toast({
        title: "Failed to finalize",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  /** Override the per-thread negotiation mode (null = inherit vendor contact mode). */
  const setModeOverrideMutation = useMutation({
    mutationFn: async ({
      thread_id,
      mode,
    }: {
      thread_id: string;
      mode: NegotiationMode | null;
    }) => {
      const { error } = await supabase
        .from("negotiation_threads")
        .update({ mode_override: mode })
        .eq("id", thread_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
      toast({ title: "Mode override updated" });
    },
    onError: (err) => {
      toast({
        title: "Failed to update mode override",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  /**
   * Manually mark the thread as declined.
   * Sets status='declined' and declined_at=now() so the cooldown clock starts.
   */
  const markDeclinedMutation = useMutation({
    mutationFn: async (thread_id: string) => {
      const { error } = await supabase
        .from("negotiation_threads")
        .update({
          status: "declined",
          declined_at: new Date().toISOString(),
        })
        .eq("id", thread_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
      toast({ title: "Negotiation marked as declined" });
    },
    onError: (err) => {
      toast({
        title: "Failed to mark declined",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    thread: data?.thread ?? null,
    messages: data?.messages ?? [],
    isLoading,
    isGeneratingInitialDraft,
    error,
    refetch,
    approveSend: (message_id: string) => approveSendMutation.mutateAsync(message_id),
    discardDraft: (message_id: string) => discardDraftMutation.mutateAsync(message_id),
    regenerateDraft: (thread_id: string) => regenerateDraftMutation.mutateAsync(thread_id),
    /** Manually trigger an AI draft (initial or reply) at any time. */
    generateDraft: (thread_id: string) => generateDraftMutation.mutateAsync(thread_id),
    editDraft: (message_id: string, updates: { subject?: string; body_text?: string }) =>
      editDraftMutation.mutateAsync({ message_id, updates }),
    finalize: (thread_id: string, opts: { agreed_apr: number; agreed_days_early: number }) =>
      finalizeMutation.mutateAsync({ thread_id, ...opts }),
    /** Override the per-thread mode (null = revert to vendor contact default). */
    setModeOverride: (thread_id: string, mode: NegotiationMode | null) =>
      setModeOverrideMutation.mutateAsync({ thread_id, mode }),
    /** Manually mark this thread declined; starts the decline cooldown clock. */
    markDeclined: (thread_id: string) => markDeclinedMutation.mutateAsync(thread_id),
    isSending: approveSendMutation.isPending,
    isDiscarding: discardDraftMutation.isPending,
    isRegenerating: regenerateDraftMutation.isPending,
    /** True while the manual generateDraft call is in flight. */
    isGeneratingDraft: generateDraftMutation.isPending,
    isFinalizing: finalizeMutation.isPending,
    isSettingModeOverride: setModeOverrideMutation.isPending,
    isMarkingDeclined: markDeclinedMutation.isPending,
  };
}
